// @ts-nocheck
// @ts-ignore

function getCookie(cname) {
  let name = cname + "=";
  let decodedCookie = decodeURIComponent(document.cookie);
  let ca = decodedCookie.split(';');
  for (let i = 0; i < ca.length; i++) {
    let c = ca[i];
    while (c.charAt(0) == ' ') {
      c = c.substring(1);
    }
    if (c.indexOf(name) == 0) {
      return c.substring(name.length, c.length);
    }
  }
  return "";
}
class CustomFromYourWishlist extends HTMLElement {

  // ─── GraphQL Fragments ──────────────────────────────────────────────────────

  // Built via helper functions so we can change the media shape
  // depending on `useSwiper` (full media set vs minimal single-image).
  static productFields(useSwiper) {
    const mediaBlock = useSwiper
      ? `
      media(first: 20) {
        edges {
          node {
            mediaContentType
            ... on MediaImage {
              image { url altText width height }
            }
            ... on Video {
              sources { url mimeType }
              previewImage { url }
            }
          }
        }
      }`
      : `
      media(first: 1) {
        edges {
          node {
            mediaContentType
            ... on MediaImage {
              image { url altText width height }
            }
          }
        }
      }`;

    return `
      title
      handle
      id
      availableForSale
      productType
      priceRange {
        maxVariantPrice { amount }
        minVariantPrice { amount }
      }
      ${mediaBlock}
      metafield_tag: metafield(namespace: "neo", key: "usage_tag") { value }
      metafield_highlights: metafield(namespace: "neo", key: "highlights") { value }
      metafield_watch_gif: metafield(namespace: "neo", key: "watch_gif") {
        reference { ... on MediaImage { image { url } } }
      }
      metafield_color_swatch_image: metafield(namespace: "custom", key: "color_swatch_image") {
        reference { ... on MediaImage { image { url } } }
      }
    `;
  }

  static productFieldsWithVariants(useSwiper) {
    const baseFields = CustomFromYourWishlist.productFields(useSwiper);
    return `
      ${baseFields}
      metafield_color_variants: metafield(namespace: "custom", key: "color_variants") {
        references(first: 10) {
          nodes {
            ... on Product {
              ${baseFields}
            }
          }
        }
      }
    `;
  }

  // ─── Lifecycle ───────────────────────────────────────────────────────────────

  constructor() {
    super();
    this.wishlistItems = [];  // Raw items from SDK
    this.sdkResponse = {};  // Full SDK response
    this.productCards = [];  // Hydrated product data from GraphQL
    this.isLoading = false;
    this.isSmartWishlistAvailableInCookies = true;
    this.pollInterval = null;
    this.pollStartTime = null;
    this.POLL_TIMEOUT_MS = 10000;
    this.POLL_TIMEOUT_INTERVAL = 800;
    this._showWatchGif = false;
    this._showWatchGifVideo = false;
    this.maxShownCards = 10;
    this.swatchMax = 3;
    this.useSwiper = false;
  }

  connectedCallback() {
    this.render();

    // Flow:
    // - If SmartWishlist cookies exist: fetch wishlist immediately, then fetch products + hydrate.
    // - If cookies don't exist yet: wait for the SDK (it may set cookies), then retry.
    this.bootstrapWishlist();
  }

  disconnectedCallback() {
    this.clearPoll('disconnectedCallback');
  }

  // ─── Rendering ───────────────────────────────────────────────────────────────

  render() {
    this.innerHTML = `
      <div class="from-your-wishlist">
        <h2 class="from-your-wishlist__title athletics-bold">From Your Wishlist</h2>
        <div id="wishlist-cards-container" class="from-your-wishlist__grid">
          ${this.renderSkeletonCards()}
        </div>
      </div>
    `;
  }

  renderSkeletonCards() {
    const count = this.wishlistItems.length || 4;
    return Array.from({ length: count }, () => `
        <div class="wl-card wl-card--skeleton">
          <div class="wl-skeleton__media">
            <span class="wl-skeleton__tag wl-skeleton__shine"></span>
            <span class="wl-skeleton__heart wl-skeleton__shine"></span>
            <div class="wl-skeleton__swatches">
              <span class="wl-skeleton__swatch wl-skeleton__shine"></span>
              <span class="wl-skeleton__swatch wl-skeleton__shine"></span>
              <span class="wl-skeleton__swatch wl-skeleton__shine"></span>
              <span class="wl-skeleton__swatch wl-skeleton__shine"></span>
            </div>
          </div>
          <div class="wl-skeleton__info">
            <span class="wl-skeleton__line wl-skeleton__shine" style="width:90%"></span>
            <span class="wl-skeleton__line wl-skeleton__shine" style="width:75%"></span>
            <span class="wl-skeleton__line wl-skeleton__shine wl-skeleton__line--sm" style="width:60%"></span>
            <span class="wl-skeleton__line wl-skeleton__shine wl-skeleton__line--sm" style="width:50%"></span>
          </div>
          <div class="wl-skeleton__btn wl-skeleton__shine"></div>
        </div>
      `).join('');
  }

  // ─── SDK Polling ─────────────────────────────────────────────────────────────

  waitForSDK() {
    this.clearPoll("initiate polling");
    this.pollStartTime = Date.now();

    this.pollInterval = setInterval(() => {
      const sdkReady = typeof SWGetCustomerWishlistId === 'function' && typeof SWGetExpressWishlistId === 'function';

      if (sdkReady) {
        this.clearPoll("SDK Loaded");
        this.init();
        return;
      }

      if (Date.now() - this.pollStartTime > this.POLL_TIMEOUT_MS) {
        this.clearPoll("SDK Not Loaded");
        console.warn('[Wishlist] SDK did not load within the expected time.');
      }
    }, this.POLL_TIMEOUT_INTERVAL);
  }

  clearPoll(invoker) {
    if (this.pollInterval) {
      clearInterval(this.pollInterval);
      this.pollInterval = null;
    }
  }

  // ─── Initialisation ──────────────────────────────────────────────────────────

  async init() {
    try {
      this.isLoading = true;

      // `bootstrapWishlist` owns the orchestration; keep `init` as a safe re-entry.
      await this.bootstrapWishlist();
    } catch (err) {
      console.error('[Wishlist] Initialisation error:', err);
    } finally {
      this.isLoading = false;
    }
  }

  // ─── Fetch Wishlist Items from SDK ───────────────────────────────────────────

  async fetchWishlistItems() {

    // ← ADD THIS GUARD
    const cache = globalThis.wishlistItemsSTC;
    if (cache && cache.forceEmpty === true) {
      this.wishlistItems = [];
      this.sdkResponse = { items: [], count: 0 };
      this.isSmartWishlistAvailableInCookies = true;
      return; // Explicit empty state; don't refetch yet.
    }

    if (cache && Array.isArray(cache.items) && cache.items.length > 0) {
      this.wishlistItems = cache.items;
      this.sdkResponse = { items: cache.items, count: cache.count ?? cache.items.length };
      this.isSmartWishlistAvailableInCookies = true;
      return; // ← skip cookie + API fetch
    }

    const customerIdRaw = getCookie('wishlist_customer_id'); // "0" when not logged in (cookie string)
    const wishlistId = getCookie('wishlist_id');


    if (!customerIdRaw || !wishlistId) {
      this.isSmartWishlistAvailableInCookies = false;
      return;
    }

    const customerId = String(customerIdRaw);
    this.isSmartWishlistAvailableInCookies = true;

    const queryParam = customerId === '0'
      ? `wishlistid=${wishlistId}`
      : `customerid=${encodeURIComponent(customerId)}`;

    this.sdkResponse = await this.callWishlistApi(queryParam);
    this.wishlistItems = this.sdkResponse.items || [];

    // Store in shared cache so other sections (e.g. cart wishlist tab) can reuse.
    globalThis.wishlistItemsSTC = {
      ...(globalThis.wishlistItemsSTC && typeof globalThis.wishlistItemsSTC === 'object' ? globalThis.wishlistItemsSTC : {}),
      items: this.wishlistItems,
      count: this.sdkResponse?.count ?? this.wishlistItems.length,
      fetchedAt: Date.now(),
      source: 'custom-from-your-wishlist',
      forceEmpty: false,
    };
  }

  async bootstrapWishlist() {
    // Attempt immediate load based on cookies first.
    await this.fetchWishlistItems();

    if (!this.isSmartWishlistAvailableInCookies) {
      // SDK may populate cookies after load; retry once SDK is ready.
      waitForWishlistSDKAndCallMyFunction(async () => {
        await this.fetchWishlistItems();
        await this._hydrateIfReady();
      });
      return;
    }

    await this._hydrateIfReady();
  }

  async _hydrateIfReady() {
    if (this.wishlistItems.length > 0) {
      await this.fetchProductsByIds();
      this.hydrate();
      return;
    }

    // No wishlist items: remove the section content.
    this.innerHTML = '';
  }

  async callWishlistApi(queryParam) {
    try {
      const res = await fetch(`/a/wishlist?type=api&${queryParam}&version=1`);
      let data = await res.json();
      if (Array.isArray(data?.items)) {
        const seen = new Set();
        data.items = data.items.filter((item) => {
          const key = String(item?.id ?? item?.product_id ?? item?.productId ?? item?.variant_id ?? item?.variantId ?? '');
          if (!key || seen.has(key)) return false;
          seen.add(key);
          return true;
        });
      }
      return data;
    } catch (err) {
      console.warn('[Wishlist] API call failed:', err);
      return {};
    }
  }

  // ─── Fetch Product Data via GraphQL ──────────────────────────────────────────

  async fetchProductsByIds() {
    const productIds = this.wishlistItems.map(item => item.id).filter(Boolean);

    if (productIds.length === 0) return;

    // Build aliases like: p0: product(id: "gid://shopify/Product/123") { ... }
    // Use different field sets depending on whether Swiper is enabled.
    const fieldsWithVariants = CustomFromYourWishlist.productFieldsWithVariants(this.useSwiper);

    const aliases = productIds.map((id, index) => {
      const gid = `gid://shopify/Product/${id}`;
      return `
          p${index}: product(id: "${gid}") {
            ${fieldsWithVariants}
          }
        `;
    }).join('\n');

    try {
      // ✅ FIXED: restored missing `await fetch(`
      const res = await fetch('/api/2024-04/graphql.json', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Shopify-Storefront-Access-Token': window.storefrontAccessToken || '',
        },
        body: JSON.stringify({ query: `{ ${aliases} }` }),
      });

      const json = await res.json();
      const data = json?.data || {};

      // Merge GraphQL data back into wishlist items by index
      this.productCards = this.wishlistItems.map((item, index) => ({
        ...item,
        graphqlProduct: data[`p${index}`] || null,
      }));

    } catch (err) {
      console.warn('[Wishlist] GraphQL fetch failed:', err);
      // Fall back to SDK data only
      this.productCards = [...this.wishlistItems];
    }
  }

  // ─── Hydrate DOM ─────────────────────────────────────────────────────────────

  hydrate() {
    const container = this.querySelector('#wishlist-cards-container');
    if (!container) return;

    const source = this.productCards.length > 0
      ? this.productCards
      : this.wishlistItems;

    if (!source || source.length === 0) {
      container.innerHTML = '';
      return;
    }

    const fragment = document.createDocumentFragment();
    source.forEach((item, index) => {
      if (index >= this.maxShownCards) return;
      fragment.appendChild(this.buildProductCard(item))
    });

    // Batch the swap into a single mutation right before paint, instead of
    // clearing/appending immediately, so style/layout only recalculates once.
    requestAnimationFrame(() => {
      container.innerHTML = '';
      container.appendChild(fragment);
      if (typeof ReloadSmartWishlist === 'function') {
        ReloadSmartWishlist();
      }

      // Initialise Swiper instances for wishlist cards when enabled
      if (this.useSwiper && typeof Swiper !== 'undefined') {
        this._initWishlistSwipers();
      }
    });
  }

  // ─── Helpers ─────────────────────────────────────────────────────────────────

  _initWishlistSwipers() {
    try {
      const swiperEls = this.querySelectorAll('.neo-product-card-media .swiper');
      swiperEls.forEach((el) => {
        if (el._wishlistSwiperInstance) return;
        el._wishlistSwiperInstance = new Swiper(el, {
          slidesPerView: 1,
          spaceBetween: 0,
        });
      });
    } catch (err) {
      console.warn('[Wishlist] Failed to initialise Swiper for wishlist cards:', err);
    }
  }

  /** Extract numeric product ID from GID or plain string */
  _getProductIdNumeric(id) {
    if (!id) return '';
    const s = String(id).trim();
    if (/^\d+$/.test(s)) return s;
    const m = s.match(/gid:\/\/shopify\/Product\/(\d+)/);
    return m ? m[1] : s.replace(/\D/g, '');
  }

  /** Format a decimal amount string as a money string */
  _formatMoney(amount) {
    const num = parseFloat(amount) || 0;
    const currency = window.Shopify?.currency?.active || 'INR';
    try {
      return new Intl.NumberFormat('en-IN', {
        style: 'currency',
        currency,
        maximumFractionDigits: 0,
      }).format(num);
    } catch {
      return `${currency} ${num.toFixed(0)}`;
    }
  }

  /** Determine watch-gif src and whether a video exists (after media index 2) */
  _getWatchGifAndHasVideo(gql) {
    const mediaEdges = gql?.media?.edges || [];
    let watchGifSrc = gql?.metafield_watch_gif?.reference?.image?.url || '';
    let hasVideo = false;

    for (let i = 0; i < mediaEdges.length; i++) {
      const node = mediaEdges[i]?.node;
      if (i <= 2 || !node) continue;
      const isVideo = node.mediaContentType === 'VIDEO' || (node.sources && !node.image);
      if (isVideo) {
        hasVideo = true;
        if (!watchGifSrc && node.previewImage?.url) watchGifSrc = node.previewImage.url;
        break;
      }
    }

    return { watchGifSrc, hasVideo };
  }

  /** Inline SVG for button corner (matches button-cornor.liquid) */
  _cornerSVG(uid) {
    return `<svg xmlns="http://www.w3.org/2000/svg" width="8" height="8" viewBox="0 0 8 8" fill="none">
        <mask id="${uid}" fill="white">
          <path d="M0 2C0 0.895431 0.895431 0 2 0H6C7.10457 0 8 0.895431 8 2V6C8 7.10457 7.10457 8 6 8H2C0.895431 8 0 7.10457 0 6V2Z"/>
        </mask>
        <path d="M-0.5 2C-0.5 0.619288 0.619288 -0.5 2 -0.5H5.5C6.88071 -0.5 8 0.619288 8 2C8 1.17157 7.10457 0.5 6 0.5H2C1.17157 0.5 0.5 1.17157 0.5 2H-0.5ZM8 8H0H8ZM2 8C0.619288 8 -0.5 6.88071 -0.5 5.5V2C-0.5 0.619288 0.619288 -0.5 2 -0.5V0.5C1.17157 0.5 0.5 1.17157 0.5 2V6C0.5 7.10457 1.17157 8 2 8ZM8 0V8V0Z" fill="#ffffff" mask="url(#${uid})"/>
      </svg>`;
  }

  // ─── Build Color Swatches ─────────────────────────────────────────────────────

  /**
   * Builds the swatch list for a product card.
   *
   * Strategy:
   *  1. Start with the current product itself if it has a swatch image.
   *  2. Append each color variant that also has a swatch image.
   *  3. Cap visible swatches at SWATCH_MAX (4); show "+N" overflow badge.
   *
   * This ensures the active product is always represented as the first swatch,
   * matching the UX expectation of "current colour + alternates".
   */
  _buildSwatchHTML(gql) {
    const SWATCH_MAX = this.swatchMax;

    // Candidate: the product itself
    const selfSwatch = gql?.metafield_color_swatch_image?.reference?.image?.url
      ? { url: gql.metafield_color_swatch_image.reference.image.url, handle: gql.handle, title: gql.title, isSelf: true }
      : null;

    // Candidates: color variant nodes that have a swatch image
    const colorVariantNodes = gql?.metafield_color_variants?.references?.nodes || [];
    const variantSwatches = colorVariantNodes
      .filter(n => n?.metafield_color_swatch_image?.reference?.image?.url)
      .map(n => ({
        url: n.metafield_color_swatch_image.reference.image.url,
        handle: n.handle,
        title: n.title,
        isSelf: false,
      }));

    // Merge — self first, then variants
    const allSwatches = [...(selfSwatch ? [selfSwatch] : []), ...variantSwatches];

    if (allSwatches.length === 0) return '';


    const uniqueSwatches = Object.values(
      allSwatches.reduce((acc, item) => {
        acc[item.url] ??= item;
        return acc;
      }, {})
    );

    const overflowCount = Math.max(0, uniqueSwatches.length - SWATCH_MAX);

    const items = uniqueSwatches.slice(0, SWATCH_MAX).map(s => {
      const safeTitle = (s.title || '').replace(/"/g, '&quot;');
      // Self swatch links to the PDP; variant swatches link to their own PDP
      const href = `/products/${s.handle}`;
      // Mark the active swatch so CSS can highlight it
      const activeAttr = s.isSelf ? ' aria-current="true"' : '';
      return `<a class="color-swatch-image-container ${s.isSelf ? 'is-active' : ''}" href="${href}" title="${safeTitle}"${activeAttr}>
          <img src="${s.url}&width=100" alt="${safeTitle}" width="24" height="24" loading="lazy">
        </a>`;
    });


    if (overflowCount > 0) {
      items.push(`<span class="wl-swatch-overflow athletics-semibold">+ ${overflowCount}</span>`);
    }

    return items.join('') || '';

  }

  // ─── Build a Single Product Card ─────────────────────────────────────────────

  buildProductCard(item) {
    const gql = item.graphqlProduct || null;
    const productId = String(item.product_id || item.id || gql?.id || '');
    const numericId = this._getProductIdNumeric(productId);
    const variantId = String(item.variant_id || '');
    const title = gql?.title || item.product_title || item.title || 'Product';
    const handle = gql?.handle || item.handle || '';
    const productUrl = item.product_url || `/products/${handle}`;


    // ── Pre-populate NeoProductCard1 cache so it doesn't re-fetch ──
    if (gql && numericId) {
      globalThis.neoProductCardCache ??= {};
      globalThis.neoProductCardCache[numericId] = gql;

      const colorNodes = gql.metafield_color_variants?.references?.nodes || [];
      for (const node of colorNodes) {
        const nid = this._getProductIdNumeric(node?.id);
        if (nid) globalThis.neoProductCardCache[nid] = node;
      }
    }

    // ── Metafields ──
    const tag = gql?.metafield_tag?.value || item.tag || '';

    const rawHighlights = gql?.metafield_highlights?.value;
    const highlights = rawHighlights
      ? (() => { try { return JSON.parse(rawHighlights); } catch { return [rawHighlights]; } })()
      : (Array.isArray(item.highlights) ? item.highlights : []);
    const highlightText = highlights.join(' | ');

    // ── Price ──
    const priceAmount = gql?.priceRange?.minVariantPrice?.amount || item.variant_price || '';
    const priceDisplay = priceAmount ? this._formatMoney(priceAmount) : '';

    // ── Color variant IDs for ATB button ──
    const colorVariantNodes = gql?.metafield_color_variants?.references?.nodes || [];
    const colorVariantIds = colorVariantNodes
      .map(n => this._getProductIdNumeric(n?.id))
      .filter(Boolean)
      .join(',');

    // ── Media edges & first image ──
    const mediaEdges = gql?.media?.edges || [];
    const firstImageEdge = mediaEdges.find(
      e => e?.node?.mediaContentType === 'IMAGE' && e?.node?.image
    );
    const firstImageUrl = firstImageEdge?.node?.image?.url
      ? `${firstImageEdge.node.image.url}${firstImageEdge.node.image.url.includes('?') ? '&' : '?'}width=320`
      : (item.featured_image?.url || item.image || '');
    const firstImageAlt = firstImageEdge?.node?.image?.altText || title;

    // For non-swiper mode, prefer the very first media image edge explicitly
    const firstRawMediaImageUrl = mediaEdges?.[0]?.node?.image?.url || '';
    const nonSwiperImageUrl = firstRawMediaImageUrl || firstImageUrl;

    // ── Watch GIF ──
    const { watchGifSrc, hasVideo } = this._getWatchGifAndHasVideo(gql);
    const showWatchGif = hasVideo && watchGifSrc;
    const watchGifUrl = showWatchGif
      ? `${watchGifSrc}${watchGifSrc.includes('?') ? '&' : '?'}width=100`
      : '';

    // ── Color swatches ──

    // ── Corner SVGs (unique mask IDs per card) ──
    const cid = numericId || productId;
    const corners = [1, 2, 3, 4]
      .map(i => `<div class="corner-${i}">${this._cornerSVG(`wl-btn-corner-${cid}-${i}`)}</div>`)
      .join('');

    // ── Loading SVG ──
    const loadingSVG = `<svg class="loading-svg" width="24" height="24" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
      <style>.spinner_aj0A{transform-origin:center;animation:spinner_KYSC .75s infinite linear}@keyframes spinner_KYSC{100%{transform:rotate(360deg)}}</style>
      <path d="M12,4a8,8,0,0,1,7.89,6.7A1.53,1.53,0,0,0,21.38,12h0a1.5,1.5,0,0,0,1.48-1.75,11,11,0,0,0-21.72,0A1.5,1.5,0,0,0,2.62,12h0a1.53,1.53,0,0,0,1.49-1.3A8,8,0,0,1,12,4Z" class="spinner_aj0A"/>
      </svg>`;

    // ── Watch play SVG ──
    const watchPlaySVG = `<svg class="neo-product-card-1-watch-play-icon" width="7" height="8" viewBox="0 0 7 8" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M0 7.08734V0.735669C0 0.149096 0.653735 -0.200773 1.14179 0.124599L5.90983 3.30329C6.36248 3.60505 6.36248 4.27019 5.90983 4.57195L1.18526 7.72167C0.67862 8.05943 0 7.69624 0 7.08734Z" fill="white"/>
      </svg>`;

    // Build Swiper slides (all IMAGE media) when Swiper is enabled
    const swiperSlidesHtml = mediaEdges
      .filter(edge => edge?.node?.mediaContentType === 'IMAGE' && edge?.node?.image?.url)
      .map(edge => {
        const img = edge.node.image;
        const url = img.url;
        const alt = img.altText || title;
        const sep = url.includes('?') ? '&' : '?';
        return `
                <div class="swiper-slide">
                  <img
                    src="${url}${sep}width=520"
                    alt="${alt.replace(/"/g, '&quot;')}"
                    class="neo-product-card-1__image-img"
                    loading="lazy"
                    width="700"
                    height="1000"
                  >
                </div>`;
      })
      .join('');

    // ── Media markup (conditional Swiper usage) ──
    const mediaMarkup = this.useSwiper
      ? `
          <div class="neo-product-card-media">
            <div class="swiper">
              <div class="swiper-wrapper">
                ${swiperSlidesHtml ||
      `
                <div class="swiper-slide">
                  ${firstImageUrl
        ? `<img
                        src="${firstImageUrl}&width=520"
                        alt="${firstImageAlt.replace(/"/g, '&quot;')}"
                        class="neo-product-card-1__image-img"
                        loading="lazy"
                        width="700"
                        height="1000"
                      >`
        : '<div class="neo-product-card-1__image-placeholder"></div>'
      }
                </div>
                `
      }
              </div>
            </div>
          </div>
        `
      : `
          <div class="neo-product-card-media">
            ${nonSwiperImageUrl
        ? `<img
                  src="${nonSwiperImageUrl}${nonSwiperImageUrl.includes('?') ? '&' : '?'}width=520"
                  alt="${firstImageAlt.replace(/"/g, '&quot;')}"
                  class="neo-product-card-1__image-img"
                  loading="lazy"
                  width="700"
                  height="1000"
                >`
        : '<div class="neo-product-card-1__image-placeholder"></div>'
      }
          </div>
        `;

    // ── Build card element ──
    const card = document.createElement('neo-product-card-1');
    card.className = 'neo-product-card-1-in-cart wl-card';
    card.setAttribute('data-product-id', numericId || productId);

    card.innerHTML = `

      <!-- ── IMAGE AREA ─────────────────────────────────────────────────── -->
        <div class="wl-card__media-wrap neo-product-card-media-container">

          ${tag ? `<p class="neo-product-card-1-tag wl-card__neo-tag athletics-light">${tag}</p>` : ''}

          <div class="wl-card__heart neo-product-card-wishlist">
            <form method="post" action="/cart/add" id="product-form-installment-${numericId || productId}"
              accept-charset="UTF-8" class="installment caption-large" enctype="multipart/form-data"><input type="hidden"
                  name="form_type" value="product"><input type="hidden" name="utf8" value="✓"><input type="hidden"
                  name="id" value="">

              <input type="hidden" name="product-id" value="${numericId || productId}">
              <input type="hidden" name="section-id"value="template--${document.querySelector('cart-items-component')?.dataset.sectionId}">
            </form>
            <span
              class="smartwishlist"
              data-product="${numericId || productId}"
              data-variant="${variantId}"
            ></span>
          </div>


          ${this._showWatchGif && this._showWatchGifVideo ? `
            <div class="neo-product-card-1-watch-gif-container">
            <div class="relative">
            <img
            src="${watchGifUrl}"
                alt="${title.replace(/"/g, '&quot;')}"
                class="neo-product-card-1-watch-gif"
                loading="lazy"
                width="100"
                height="100"
              >
              ${watchPlaySVG}
            </div>
            <p class="neo-product-card-1-watch-gif-text">Watch</p>
            </div>` : ''}

          ${mediaMarkup}

        </div>

        <!-- ── TEXT INFO ──────────────────────────────────────────────────── -->
        <div class="wl-card__info">
          <a class="title-container wl-card__title-link" href="${productUrl}">
            <h2 class="visually-hidden">${title}</h2>
            <p class="neo-product-card-title wl-card__title athletics-bold">${title}</p>
            <p class="neo-product-card-sub-title wl-card__highlights athletics-medium">${highlightText}</p>
            </a>
            </div>

            <!-- ── SWATCH AND ADD TO CART  ──────────────────────────────────────────────────── -->

            <div class="flex flex-col justify-between wl-card__swatch-and-add-to-cart-row">
              <div id="color-variant-images-wishlist">
             ${this._buildSwatchHTML(gql)}
            </div>

            <div class="wl-card__atb-row">
              ${priceDisplay ? `<span class="wl-card__price athletics-bold">${priceDisplay}</span>` : '<span class="wl-card__price">00</span>'}
              <button
                type="button"
                class="wl-card__atb-btn athletics-semibold"
                onclick="ATBModalOpen(event, '${numericId || productId}');
                document.querySelector('cart-drawer-component').close();"
                data-product-id="${numericId || productId}"
                data-color-variants-ids="${colorVariantIds}"
              >
                <span class="wl-card__atb-btn-inner">
                  <span class="wl-card__atb-plus athletics-semibold">+</span>
                  <span class="wl-card__atb-loading athletics-semibold">${loadingSVG}</span>
                  Add to Cart
                </span>
              </button>
            </div>

            </div>

            <!-- ── ADD TO CART ────────────────────────────────────────────────── -->


            `;
    let wlButton = card.querySelector('.smartwishlist')

    wlButton.addEventListener('click', (e) => {
      console.log('event started', e);
      const productId = e.target?.closest('[data-product]')?.getAttribute('data-product') || '';
      console.log('productId', productId);

      const prevCache = (globalThis.wishlistItemsSTC && typeof globalThis.wishlistItemsSTC === 'object')
        ? globalThis.wishlistItemsSTC
        : { items: [], count: 0 };
      console.log('prevCache', prevCache);

      const nextItems = Array.isArray(prevCache.items)
        ? prevCache.items.filter((item) => String(item?.id) !== String(productId))
        : [];
      console.log('nextItems', nextItems);

      const nextCount = nextItems.length;
      console.log('nextCount', nextCount);

      // Update shared cache immediately. If it becomes empty, mark it as authoritative
      // so other components don't re-fetch right away.
      globalThis.wishlistItemsSTC = {
        ...prevCache,
        source: 'smartwishlist-click-update',
        items: nextItems,
        count: nextCount,
        fetchedAt: Date.now(),
        forceEmpty: nextCount === 0,
      };
      console.log('globalThis.wishlistItemsSTC', globalThis.wishlistItemsSTC);

      // Update this section UI without re-fetching.
      if (nextCount === 0) {
        console.log(this.innerHTML);
        this.innerHTML = '';
        return;
      } else {
        console.log('card removed');
        card.remove();
        // this.reset();
      }

      // Re-init cart wishlist tab (it will respect forceEmpty + cache).
      if (globalThis.customCartWishlistInstance && typeof globalThis.customCartWishlistInstance.init === 'function') {
        globalThis.customCartWishlistInstance.init();
      }
    }, true)

    return card;
  }

  // ─── Public Reset ─────────────────────────────────────────────────────────────

  reset() {
    this.clearPoll('reset');
    this.wishlistItems = [];
    this.sdkResponse = {};
    this.productCards = [];
    this.isLoading = false;
    this.connectedCallback();
  }
}

// ─── Register Element ───────────────────────────────────────────────────────────

customElements.define('custom-from-your-wishlist', CustomFromYourWishlist);

// ─── Global Reset Helper ────────────────────────────────────────────────────────

document.addEventListener('DOMContentLoaded', () => {
  const el = document.querySelector('custom-from-your-wishlist');
  if (el) window.resetWishlistSection = () => el.reset();
});